import type { CreateFileFormValuesApi } from '../ui/attachments/lib/types';
import type { KnowledgeBaseInfo } from '@shared/api';
import { store } from '@shared/redux';
import i18n from '@shared/services/i18n/i18n';
import { notify } from '@app/providers/notification/notification';

interface UploadFileParams {
  file: File;
  formValues: CreateFileFormValuesApi;
  organizationId: number;
  signal?: AbortSignal;
  onUploadProgress?: (percentage: number) => void;
  onComplete?: () => void;
  onError?: (error: unknown) => void;
  onAbort?: () => void;
  onEnd?: () => void;
}

export const uploadFile = async ({
  file,
  formValues,
  organizationId,
  signal,
  onUploadProgress,
  onComplete,
  onError,
  onAbort,
  onEnd,
}: UploadFileParams): Promise<KnowledgeBaseInfo> => {
  const formData = new FormData();
  const url = `${window.location.origin}/api/knowledge/file/upload`;
  // const state = api.getState() as RootState;
  const accessToken = store.getState().auth.authInfo?.access_token;

  formData.append('uploaded_file', file);
  formData.append('title', formValues?.title);
  formData.append('description', formValues?.description);
  formData.append('organization_id', organizationId.toString());

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const handleAbort = () => xhr.abort();

    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentage = Math.floor((event.loaded / event.total) * 100);
        onUploadProgress?.(percentage);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const result = JSON.parse(xhr.responseText) as KnowledgeBaseInfo;

        onComplete?.();
        resolve(result);
      } else {
        const message = i18n.t('common.upload.serverError', { status: xhr.status });
        const error = new Error(message);
        notify({ message });
        onError?.(error);
        reject(error);
      }
    };

    xhr.onerror = () => {
      const message = i18n.t('common.upload.networkError');
      const error = new Error(message);
      notify({ message });
      onError?.(error);
      reject(error);
    };

    xhr.onabort = () => {
      onAbort?.();
      reject(new Error(i18n.t('common.upload.aborted')));
    };

    if (signal) {
      signal.addEventListener('abort', handleAbort);
      if (signal.aborted) handleAbort();
    }

    xhr.onloadend = () => {
      if (signal) {
        signal.removeEventListener('abort', handleAbort);
      }
      onEnd?.();
    };

    xhr.send(formData);
  });
};
